import { useEffect, useState } from "react";

/**
 * Page Table of Contents with active-section highlight on scroll.
 * `items`: [[anchorId, label], ...] where anchorId is a `#fragment`.
 * Uses IntersectionObserver to toggle the active entry based on which
 * heading is currently in the viewport's upper third.
 *
 * SSG-friendly: renders full markup on the server; the observer only
 * attaches on the client and degrades gracefully when JS is disabled.
 */
export default function TableOfContents({ items, ariaLabel = "Sommaire de la page" }) {
    const [active, setActive] = useState(items[0]?.[0]?.slice(1) || "");

    useEffect(() => {
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

        const targets = items
            .map(([href]) => document.getElementById(href.slice(1)))
            .filter(Boolean);
        if (targets.length === 0) return;

        const visible = new Map(); // id → intersectionRatio

        const observer = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) visible.set(e.target.id, e.intersectionRatio);
                    else visible.delete(e.target.id);
                }
                if (visible.size === 0) return;
                // Pick the entry currently most "in view" (highest ratio).
                let bestId = "";
                let bestRatio = -1;
                for (const [id, ratio] of visible) {
                    if (ratio > bestRatio) { bestRatio = ratio; bestId = id; }
                }
                if (bestId) setActive(bestId);
            },
            // Trigger when a heading enters the upper half of the viewport.
            { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
        );

        targets.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [items]);

    return (
        <nav
            aria-labelledby="toc-h2"
            aria-label={ariaLabel}
            className="lg:col-span-5 card-soft p-6 lg:p-7 bg-[var(--paper)]"
            data-testid="table-of-contents"
        >
            <p id="toc-h2" className="text-xs uppercase tracking-[0.18em] text-[var(--navy)] font-bold mb-4">
                <i className="fa-solid fa-list-ul mr-1.5 text-[var(--amber)]" />Sur cette page
            </p>
            <ol className="space-y-2 text-sm">
                {items.map(([href, label], idx) => {
                    const id = href.slice(1);
                    const isActive = active === id;
                    return (
                        <li key={href} className="flex items-baseline gap-2">
                            <span
                                className={`font-bold text-xs transition-colors ${isActive ? "text-[var(--amber)]" : "text-[var(--ink-soft)]/60"}`}
                                aria-hidden="true"
                            >
                                {String(idx + 1).padStart(2, "0")}
                            </span>
                            <a
                                href={href}
                                aria-current={isActive ? "location" : undefined}
                                className={`hover:text-[var(--amber)] hover:underline underline-offset-2 transition-colors ${
                                    isActive
                                        ? "text-[var(--amber)] font-semibold"
                                        : "text-[var(--ink)]"
                                }`}
                                data-testid={`toc-link-${idx}`}
                            >
                                {label}
                            </a>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
